import * as formulaParser from 'hot-formula-parser';
import { FormulaVariable } from './formula-variable';
import { FormulaResponse } from './formula-response';

/**
 * Object to execute some formula with given variables.
 * @example 
const runner = new FormulaRunner('SUM([D],[C])');
runner.addVariable({identifier: 'A', value: 10, formula: null});
runner.addVariable({identifier: 'B', value: 5, formula: null});
runner.addVariable({identifier: 'C', value: null, formula: '[A]+[B]'});
runner.addVariable({identifier: 'D', value: null, formula: '[C] * 2'});
runner.run()
	.then(response => console.log(response.result))
 */
export class FormulaRunner {

	private static VAR_FUNCTION_SEPARATOR: string = '_';
	private _variables: Map<string, FormulaVariable[]>;

	constructor(private _resultFormula: string) {
		this._variables = new Map();
	}

	/**
	 * Add a FormulaVariable to the runner.
	 * @param variable FormulaVariable
	 */
	addVariable(variable: FormulaVariable): void {
		const variables = this._variables.get(variable.identifier) || [];
		variables.push(variable);
		this._variables.set(variable.identifier, variables);
	}

	/**
	 * Modifies value of variable with the given identifier (only if has only one variable with the identifier)
	 * @param identifier Identifier of the variable to modify value
	 * @param value Value to be set
	 */
	setValue(identifier: string, value: any): void {
		const variables = this._variables.get(identifier);
		if (variables && variables.length == 1) {
			variables[0].value = value;
		}
	}

	/**
	 * Runs the expression and return the result for the given formula
	 */
	async run(): Promise<FormulaResponse> {
		var parser = new formulaParser.Parser();

		this._variables.forEach((variables: FormulaVariable[], identifier: string) => {
			let i = 0;
			for (let variable of variables) {
				if (variable.value) {
					parser.setFunction(identifier + FormulaRunner.VAR_FUNCTION_SEPARATOR + i, () => variable.value);
				}
				i++;
			}
		});

		try {
			const translatedFormula = this.translateFormula(new String(this._resultFormula));
			const exec = parser.parse(translatedFormula);
			let message = this.translateErrorMessage(exec.error) || 'Success';
			return {
				isValid: exec.error == null,
				message: message,
				result: exec.result
			};
		} catch (error) {
			return {
				isValid: false,
				message: error,
				result: null
			};
		}
	}

	/**
	 * Translates the formula written by user, to be readed inside de API
	 * @param formula
	 */
	private translateFormula(formula): string {
		const formulaWithSpaceBetweenTokens = formula.replace(/\[/g, ' \[').replace(/\]/g, '\] ');
		const tokens = formulaWithSpaceBetweenTokens.match(/\[\S+\]/g);

		if (tokens) {
			for (const keyVar of tokens) {
				const identifier = keyVar.replace('[', '').replace(']', '').toUpperCase();//12.1.24
				const variables = this._variables.get(identifier);

				const variable = variables[0];
				if (variable.value) {
					const statement = variables.map((item, i, arr) => item.identifier + '_' + i + '()').join(',');
					formula = formula.replace(keyVar, statement);
				} else if (variable.formula) {
					formula = formula.replace(keyVar, `(${this.translateFormula(variable.formula)})`);
				} else {
					throw new Error(`Variable ${identifier} has no value.`)
				}
			}
		}
		return formula;
	}

	/**
	 * This method translates the error object from hot-parser-formula to an string message
	 * @param error 
	 */
	private translateErrorMessage(error: any): string {
		switch (error) {
			case '#ERROR!':
				return 'General error on expression.';
			case '#DIV/0!':
				return 'Divide by zero error.';
			case '#NAME?':
				return 'Not recognised variable name.';
			case '#N/A':
				return 'Some value is not available to the expression.';
			case '#NUM!':
				return 'Invalid number.';
			case '#VALUE!':
				return 'Some value is not available to the expression.';
		}
	}
}